import { useInfiniteQuery, QueryKey, InfiniteData, keepPreviousData } from "@tanstack/react-query";

import { User } from "../../../../types/types";
import { Comment } from "../../types/comment.type";

import { getComments, CommentResponse } from "../services/commentServices";

import { FEED_QUERY_KEYS } from "../queryKeys";

const COMMENT_PAGE_LIMIT = 10;

interface HookProps {
  postId: number;
  enabled?: boolean;
}

type PageParam = number;
interface TransformedData extends InfiniteData<CommentResponse, PageParam> {
  comments: Comment[];
  mentionedUsers: Partial<User>[];
}

export const useCommentListQuery = ({ postId, enabled = true }: HookProps) => {
  return useInfiniteQuery<CommentResponse, Error, TransformedData, QueryKey, PageParam>({
    queryKey: FEED_QUERY_KEYS.comments(postId),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await getComments(COMMENT_PAGE_LIMIT, postId, pageParam);
      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = allPages.reduce((acc, page) => acc + page.items.length, 0);
      const totalItems = lastPage.total;

      if (currentOffset < totalItems) {
        return currentOffset;
      }
      return undefined;
    },
    staleTime: 1000 * 60 * 2,
    select: (data) => {
      return {
        ...data,
        comments: data.pages.flatMap((page) => page.items) || [],
        mentionedUsers: data.pages.flatMap((page) => page.mentionedUsers) || [],
      };
    },
    enabled: !!postId && enabled,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
};
